<script setup>
// core
import {ref} from "vue";

// components
import CallTypeIcon from "@/components/Telephony/CallTypeIcon.vue";
import CallNumberView from "@/components/Telephony/CallNumberView.vue";
import AudioPlayer from "@/components/Recurring/AudioPlayer.vue";
import BaseFadeScroll from "@/components/ui/BaseFadeScroll.vue";

// composables
import useCallDate from "@/components/Telephony/api/useCallDate";

// constants
import {ANSWERED} from "@/components/Telephony/constants";

defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    }
})

const {formatDate, formatTime, formatDay, recordName} = useCallDate()
const expandedItems = ref(new Set());

const toggleItem = (id) => {
    if (expandedItems.value.has(id)) {
        expandedItems.value.delete(id);
    } else {
        expandedItems.value.add(id);
    }
}

const getDuration = (item) => item.status === ANSWERED && item.duration ? item.duration : '—'

const getExtension = (item) => item.status === ANSWERED ? item.extension : '—'
</script>

<template>
    <ul class="calls-m">
        <li
            v-for="(item, i) in data"
            :key="i"
            class="calls-m__item"
            :class="{ 'is-open': expandedItems.has(i) }"
        >
            <div class="calls-m__header" @click="toggleItem(i)">
                <div class="calls-m__left">
                    <span
                        class="calls-m__icon"
                        :title="`${$t(`telephony.call_types.${item.call_type}`)} · ${$t(`telephony.call_statuses.${item.status}`)}`"
                    >
                        <CallTypeIcon :item="item"/>
                    </span>

                    <span class="calls-m__who">
                        <CallNumberView class="calls-m__client" :number="item.contact"/>
                        <span class="calls-m__sub">
                            {{ $t(`telephony.call_types.${item.call_type}`) }} ·
                            <span :class="`--${item.status}`">{{ $t(`telephony.call_statuses.${item.status}`) }}</span>
                        </span>
                    </span>
                </div>

                <div class="calls-m__right">
                    <span class="calls-m__when">
                        <b>{{ formatDay(item.datetime) }}</b>
                        <span>{{ formatTime(item.datetime) }}</span>
                    </span>
                    <i class="calls-m__arrow bi bi-chevron-down"></i>
                </div>
            </div>

            <!-- stays in the DOM so the height can animate from 0fr to 1fr -->
            <div class="calls-m__body" :inert="!expandedItems.has(i)">
                <div class="calls-m__inner">
                    <div class="calls-m__rows">
                        <div class="calls-m__row">
                            <span class="item_title">{{ $t('telephony.calls_history.columns.1') }}</span>
                            <CallNumberView class="calls-m__value calls-m__number" :number="item.number"/>
                        </div>

                        <div class="calls-m__row">
                            <span class="item_title">{{ $t('telephony.calls_history.columns.0') }}</span>
                            <span class="calls-m__value">{{ formatDate(item.datetime) }} {{ formatTime(item.datetime) }}</span>
                        </div>

                        <div class="calls-m__row">
                            <span class="item_title">{{ $t('telephony.calls_history.columns.4') }}</span>
                            <span class="calls-m__value">{{ getDuration(item) }}</span>
                        </div>

                        <div class="calls-m__row">
                            <span class="item_title">{{ $t('telephony.calls_history.columns.5') }}</span>
                            <BaseFadeScroll class="calls-m__value calls-m__sip" :text="getExtension(item)"/>
                        </div>
                    </div>

                    <div v-if="item.recording_url" class="calls-m__record">
                        <span class="item_title">{{ $t('telephony.calls_history.columns.6') }}</span>
                        <AudioPlayer class="calls-m__player" :src="item.recording_url" :download-name="recordName(item)"/>
                    </div>
                </div>
            </div>
        </li>
    </ul>
</template>

<style scoped lang="scss">
.calls-m {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
    padding: 0;
    list-style: none;

    // html sets Figtree, which the project does not ship
    font-family: 'Gilroy', sans-serif;

    &__item {
        // the card lays its fields out by its own width, not the window's
        container-type: inline-size;

        background-color: var(--block-color);
        border: 1px solid transparent;
        border-radius: 11px;
        overflow: hidden;
        transition: border-color 0.3s ease;

        &.is-open {
            border-color: var(--general-color);
        }
    }

    &__header {
        padding: 12px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        cursor: pointer;
    }

    &__left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    &__icon {
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background-color: #ffffff;
    }

    &__who {
        display: flex;
        flex-direction: column;
        min-width: 0;
        line-height: 1.25;
    }

    // nested to outweigh the centring in CallNumberView's own scoped styles
    &__who &__client {
        justify-content: flex-start;
        gap: 6px;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0.05rem;
        white-space: nowrap;
        color: var(--text-color);
    }

    // wraps instead of cutting a long status like "answered in app"
    &__sub {
        font-size: 13px;
        color: var(--text-light-color);
    }

    .--answered {
        color: #70DF7B;
    }

    .--missed {
        color: #DF7070;
    }

    .--answered_elsewhere {
        color: var(--border-primary-color);
    }

    &__right {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
    }

    &__when {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        line-height: 1.25;

        b {
            font-size: 14px;
            font-weight: 500;
            color: var(--text-color);
        }

        span {
            font-size: 12px;
            color: var(--text-light-color);
        }
    }

    &__arrow {
        font-size: 12px;
        line-height: 1;
        color: var(--border-color);
        transition: transform 0.3s ease;

        .is-open & {
            transform: rotate(180deg);
        }
    }

    &__body {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease-out;

        .is-open & {
            grid-template-rows: 1fr;
        }
    }

    &__inner {
        min-height: 0;
        overflow: hidden;
        padding: 0 16px;

        .is-open & {
            padding-bottom: 16px;
        }
    }

    &__rows {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-top: 12px;
        border-top: 1px solid #e3e0f5;
    }

    &__row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        min-width: 0;
        min-height: 24px;

        .item_title {
            flex-shrink: 0;
        }
    }

    &__value {
        min-width: 0;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.05rem;
        text-align: right;
        white-space: nowrap;
        color: var(--text-color);
    }

    &__row &__number {
        justify-content: flex-end;
        gap: 6px;
    }

    &__sip {
        font-variant-numeric: tabular-nums;
    }

    &__record {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 12px;
    }

    // the player sits on the light card, so it takes the white of the inner
    // blocks; nested to outweigh AudioPlayer's own background
    &__record &__player {
        background: #ffffff;
    }
}

// .item_title from SettingsNumberDialog
.item_title {
    display: flex;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--border-primary-color);
}

// fields go two, then four in a row, label above value
@container (min-width: 480px) {
    .calls-m__rows {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px 20px;
    }

    .calls-m__row {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 2px;
    }

    .calls-m__value {
        max-width: 100%;
        text-align: left;
    }

    .calls-m__row .calls-m__number {
        justify-content: flex-start;
    }
}

// four in a row, the player on one line with its label
@container (min-width: 640px) {
    .calls-m__rows {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .calls-m__record {
        flex-direction: row;
        align-items: center;
        gap: 16px;
        padding-top: 12px;
        border-top: 1px solid #e3e0f5;
    }

    .calls-m__player {
        flex: 1;
        min-width: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .calls-m__body,
    .calls-m__arrow {
        transition: none;
    }
}
</style>
